import { useState, useEffect } from "react";
import { AlertCircle, DollarSign } from "lucide-react";
import { cn } from "@/lib/utils";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useAuth } from "@/contexts/AuthContext";
import { useProgramYear } from "@/contexts/ProgramYearContext";

import { ScoreGauge } from "./ScoreGauge";
import { PaymentCurveChart } from "./PaymentCurveChart";
import { GroupVsIndividualComparison } from "./GroupVsIndividualComparison";
import { ClinicianBreakdownTable, type ClinicianData } from "./ClinicianBreakdownTable";
import { ImprovementOpportunity } from "./ImprovementOpportunity";
// [Data-freshness 2026-08-12] Seed the config from the shared React Query
// cache (replaces the deleted module-level paymentDataCache).
import { queryClient } from "@/lib/query-client";
import { qk } from "@/lib/query-keys";

interface PaymentAdjustmentModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  compositeScore: number;
}

interface PaymentConfig {
  performance_year: number;
  payment_year: number;
  low_scenario_input: number;
  scaling_factor: number;
}

interface PracticeEligibility {
  allowed_charges: number;
  practice_name: string;
  tin: string;
}

interface ClinicianEligibility {
  npi: string;
  clinician_name: string;
  clinician_type: string;
  mips_eligible_individual: boolean;
  mips_eligible_group: boolean;
  allowed_charges: number;
}

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

// Payment-adjustment math lives in the shared lib (R10 extract 2026-08-03)
import {
  computeAdjustmentPct as calculateAdjustment,
  determineZone,
  MAX_PENALTY,
} from '@/lib/payment-adjustment';

function formatCurrency(amount: number): string {
  const absAmount = Math.abs(amount);
  const formatted = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(absAmount);
  
  if (amount < 0) return '-' + formatted;
  if (amount > 0) return '+' + formatted;
  return formatted;
}

// Skeleton component that matches the modal layout structure
function ModalSkeleton() {
  return (
    <div className="space-y-6">
      {/* Score and Projection Header */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Score Display Skeleton */}
        <div className="space-y-4">
          <div className="text-center">
            <Skeleton className="h-4 w-32 mx-auto mb-2" />
            <Skeleton className="h-16 w-20 mx-auto" />
          </div>
          {/* Score Gauge Skeleton */}
          <div className="space-y-2">
            <Skeleton className="h-4 w-full" />
            <div className="flex justify-between">
              <Skeleton className="h-3 w-8" />
              <Skeleton className="h-3 w-8" />
              <Skeleton className="h-3 w-8" />
            </div>
          </div>
        </div>

        {/* Projection Display Skeleton */}
        <div className="rounded-2xl bg-muted p-6 flex flex-col items-center justify-center">
          <Skeleton className="h-4 w-40 mb-3" />
          <Skeleton className="h-12 w-32 mb-2" />
          <Skeleton className="h-3 w-48 mb-1" />
          <Skeleton className="h-3 w-36" />
        </div>
      </div>

      <Separator />

      {/* Chart Skeleton */}
      <div>
        <Skeleton className="h-4 w-40 mb-3" />
        <Skeleton className="h-48 w-full rounded-lg" />
      </div>

      <Separator />

      {/* Group vs Individual Skeleton */}
      <div>
        <Skeleton className="h-5 w-56 mb-4" />
        <div className="grid grid-cols-2 gap-4">
          <div className="rounded-lg border p-4">
            <Skeleton className="h-4 w-24 mb-2" />
            <Skeleton className="h-8 w-28 mb-1" />
            <Skeleton className="h-3 w-20" />
          </div>
          <div className="rounded-lg border p-4">
            <Skeleton className="h-4 w-24 mb-2" />
            <Skeleton className="h-8 w-28 mb-1" />
            <Skeleton className="h-3 w-20" />
          </div>
        </div>
      </div>

      <Separator />

      {/* Table Skeleton */}
      <div>
        <Skeleton className="h-5 w-40 mb-4" />
        <div className="space-y-2">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="flex items-center gap-4 py-2">
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-4 w-20 ml-auto" />
            </div>
          ))}
        </div>
      </div>

      {/* Disclaimer Skeleton */}
      <Skeleton className="h-24 w-full rounded-lg" />
    </div>
  );
}

export function PaymentAdjustmentModal({
  open,
  onOpenChange,
  compositeScore
}: PaymentAdjustmentModalProps) {
  const { token, selectedPractice } = useAuth();
  const { programYear } = useProgramYear();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [config, setConfig] = useState<PaymentConfig | null>(null);
  const [practiceEligibility, setPracticeEligibility] = useState<PracticeEligibility | null>(null);
  const [clinicians, setClinicians] = useState<ClinicianEligibility[]>([]);
  const [hasEligibilityData, setHasEligibilityData] = useState(false);

  const practiceId = selectedPractice?.id;
  const paymentYear = programYear + 2;
  const score = Math.round(Math.min(100, Math.max(0, compositeScore)));

  // Fetch data when modal opens
  useEffect(() => {
    if (!open || !token || !practiceId) return;

    // Seed from the shared query cache (populated by UnifiedScoreCard /
    // Insights via usePaymentAdjustmentConfig) — no loading flash when warm.
    const cachedConfig = queryClient.getQueryData<{
      low_scenario_input: number;
      scaling_factor: number;
    } | null>(qk.paymentAdjConfig(programYear));

    const fetchData = async () => {
      // If we have cached config, use it immediately (no loading flash)
      if (cachedConfig) {
        setConfig({
          performance_year: programYear,
          payment_year: programYear + 2,
          low_scenario_input: cachedConfig.low_scenario_input,
          scaling_factor: cachedConfig.scaling_factor,
        });
      } else {
        setLoading(true);
      }
      setError(null);

      try {
        // Fetch payment adjustment config (skip if cached)
        let configData = null;
        if (cachedConfig) {
          configData = {
            performance_year: programYear,
            payment_year: programYear + 2,
            low_scenario_input: cachedConfig.low_scenario_input,
            scaling_factor: cachedConfig.scaling_factor,
          };
        } else {
          const configResponse = await fetch(
            `${API_URL}/api/admin/payment-adjustment-config/${programYear}`,
            { headers: { 'Authorization': `Bearer ${token}` } }
          );

          if (!configResponse.ok) {
            if (configResponse.status === 404) {
              setError('Payment adjustment configuration not found for this year. Please contact your administrator.');
              setLoading(false);
              return;
            }
            throw new Error('Failed to fetch configuration');
          }
          configData = await configResponse.json();
        }
        setConfig(configData);

        // Fetch eligibility data (always fetch for full clinician details)
        const eligibilityResponse = await fetch(
          `${API_URL}/api/practices/${practiceId}/eligibility/${programYear}`,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );

        if (eligibilityResponse.ok) {
          const eligibilityData = await eligibilityResponse.json();
          if (eligibilityData.imported && eligibilityData.practice) {
            setPracticeEligibility({
              allowed_charges: eligibilityData.practice.allowed_charges || 0,
              practice_name: eligibilityData.practice.practice_name || '',
              tin: eligibilityData.practice.tin || '',
            });
            setClinicians(eligibilityData.clinicians || []);
            setHasEligibilityData(true);
          } else {
            setHasEligibilityData(false);
          }
        }
      } catch (err) {
        console.error('Error fetching payment adjustment data:', err);
        setError('Failed to load payment adjustment data. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [open, token, practiceId, programYear]);

  // Calculate values
  const medicareRevenue = practiceEligibility?.allowed_charges || 0;
  const lowScenarioInput = config?.low_scenario_input || 2.5;
  const scalingFactor = config?.scaling_factor || 0.74;
  
  const adjustmentPercent = calculateAdjustment(score, lowScenarioInput, scalingFactor);
  const dollarAmount = medicareRevenue * adjustmentPercent;
  const zone = determineZone(score);

  // Calculate group vs individual comparison
  const groupScore = score;
  const groupDollarImpact = dollarAmount;

  // Process clinician data for comparison
  const eligibleClinicians: ClinicianData[] = clinicians
    .filter(c => c.mips_eligible_individual)
    .map(c => {
      // For now, use group score for all clinicians (individual scores would come from NPI-level calculation)
      const clinicianScore = score; // TODO: Replace with actual NPI-level scores when available
      const clinicianAdjustment = calculateAdjustment(clinicianScore, lowScenarioInput, scalingFactor);
      const clinicianImpact = (c.allowed_charges || 0) * clinicianAdjustment;
      
      return {
        npi: c.npi,
        name: c.clinician_name,
        specialty: c.clinician_type,
        mipsEligible: true,
        score: clinicianScore,
        medicareRevenue: c.allowed_charges || 0,
        adjustmentPercent: clinicianAdjustment,
        dollarImpact: clinicianImpact,
        zone: determineZone(clinicianScore),
      };
    });

  const ineligibleClinicians: ClinicianData[] = clinicians
    .filter(c => !c.mips_eligible_individual)
    .map(c => ({
      npi: c.npi,
      name: c.clinician_name,
      specialty: c.clinician_type,
      mipsEligible: false,
      score: null,
      medicareRevenue: c.allowed_charges || 0,
      reason: 'Not MIPS eligible as individual',
    }));

  const individualTotal = eligibleClinicians.reduce((sum, c) => sum + (c.dollarImpact || 0), 0);
  const difference = individualTotal - groupDollarImpact;
  const recommendation: 'GROUP' | 'INDIVIDUAL' | 'EITHER' = 
    difference > 100 ? 'INDIVIDUAL' : 
    difference < -100 ? 'GROUP' : 
    'EITHER';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] p-0 gap-0">
        <DialogHeader className="px-6 py-4 border-b">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                <DollarSign className="h-5 w-5 text-primary" />
              </div>
              <div>
                <DialogTitle className="text-xl">
                  {programYear} MIPS Payment Adjustment Projection
                </DialogTitle>
                <p className="text-sm text-muted-foreground mt-0.5">
                  {practiceEligibility?.practice_name || selectedPractice?.tin_name || 'Practice'} • 
                  Payment Year {paymentYear}
                </p>
              </div>
            </div>
          </div>
        </DialogHeader>

        <ScrollArea className="flex-1 max-h-[calc(90vh-120px)]">
          <div className="p-6 space-y-6">
            {loading ? (
              <ModalSkeleton />
            ) : error ? (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            ) : (
              <>
                {/* Eligibility Warning */}
                {!hasEligibilityData && (
                  <Alert>
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>
                      Medicare revenue data is not available. To see accurate projections, please upload your QPP eligibility file in{' '}
                      <strong>Configuration → QPP Reports</strong>.
                    </AlertDescription>
                  </Alert>
                )}

                {/* Score and Projection Header */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Score Display */}
                  <div className="space-y-4">
                    <div className="text-center">
                      <p className="text-sm text-muted-foreground">MIPS Composite Score</p>
                      <p className="text-6xl font-bold mt-1">{score}</p>
                    </div>
                    <ScoreGauge score={score} />
                  </div>

                  {/* Projection Display */}
                  <div className={cn(
                    "rounded-2xl p-6 flex flex-col items-center justify-center",
                    dollarAmount < 0 ? "bg-red-50 dark:bg-red-900/20" :
                    dollarAmount > 0 ? "bg-emerald-50 dark:bg-emerald-900/20" :
                    "bg-muted"
                  )}>
                    <p className="text-sm text-muted-foreground mb-2">
                      {dollarAmount >= 0 ? 'Projected Bonus' : 'Projected Penalty'}
                    </p>
                    <p className={cn(
                      "text-5xl font-bold",
                      dollarAmount < 0 ? "text-red-600 dark:text-red-400" :
                      dollarAmount > 0 ? "text-emerald-600 dark:text-emerald-400" :
                      "text-muted-foreground"
                    )}>
                      {hasEligibilityData ? formatCurrency(dollarAmount) : '—'}
                    </p>
                    <p className="text-sm text-muted-foreground mt-2">
                      {(adjustmentPercent * 100).toFixed(2)}% adjustment (Medium scenario)
                    </p>
                    {hasEligibilityData && (
                      <p className="text-xs text-muted-foreground mt-1">
                        Based on {formatCurrency(medicareRevenue).replace(/[+-]/, '')} Medicare revenue
                      </p>
                    )}
                  </div>
                </div>

                {/* Total Financial Impact Banner - for bonus zone */}
                {hasEligibilityData && score > 75 && (
                  <div className="rounded-lg border border-emerald-200 dark:border-emerald-800 bg-gradient-to-r from-emerald-50/80 via-blue-50/50 to-emerald-50/80 dark:from-emerald-900/10 dark:via-blue-900/10 dark:to-emerald-900/10 px-5 py-3">
                    <div className="flex flex-wrap items-center justify-between gap-4">
                      <div className="flex items-center gap-3">
                        <DollarSign className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
                        <span className="text-sm font-medium text-slate-700 dark:text-slate-300">Projected Value of Performance:</span>
                      </div>
                      
                      <div className="flex items-center gap-4 text-sm">
                        <div className="flex items-center gap-1.5">
                          <span className="text-muted-foreground">Bonus:</span>
                          <span className="font-semibold text-emerald-600 dark:text-emerald-400">{formatCurrency(dollarAmount)}</span>
                        </div>
                        <span className="text-muted-foreground">+</span>
                        <div className="flex items-center gap-1.5">
                          <span className="text-muted-foreground">Penalty avoided:</span>
                          <span className="font-semibold text-blue-600 dark:text-blue-400">{formatCurrency(Math.abs(medicareRevenue * MAX_PENALTY)).replace(/[+-]/, '')}</span>
                        </div>
                        <span className="text-muted-foreground">=</span>
                        <div className="flex items-center gap-1.5 pl-2 border-l border-slate-300 dark:border-slate-600">
                          <span className="font-bold text-lg text-slate-800 dark:text-slate-200">
                            {formatCurrency(dollarAmount + Math.abs(medicareRevenue * MAX_PENALTY)).replace(/[+-]/, '')}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <Separator />

                {/* Payment Curve Chart */}
                {hasEligibilityData && config && (
                  <>
                    <div>
                      <h3 className="text-sm font-medium mb-3">Payment Adjustment Curve</h3>
                      <PaymentCurveChart
                        currentScore={score}
                        lowScenarioInput={lowScenarioInput}
                        medicareRevenue={medicareRevenue}
                        scalingFactor={scalingFactor}
                      />
                    </div>
                    <Separator />
                  </>
                )}

                {/* Group vs Individual Comparison */}
                <GroupVsIndividualComparison
                  groupScore={groupScore}
                  groupDollarImpact={groupDollarImpact}
                  groupZone={zone}
                  individualTotal={individualTotal}
                  eligibleClinicianCount={eligibleClinicians.length}
                  ineligibleClinicianCount={ineligibleClinicians.length}
                  recommendation={recommendation}
                  differenceAmount={Math.abs(difference)}
                  hasEligibilityData={hasEligibilityData && clinicians.length > 0}
                />

                {/* Clinician Breakdown */}
                {hasEligibilityData && clinicians.length > 0 && (
                  <>
                    <Separator />
                    <ClinicianBreakdownTable
                      eligibleClinicians={eligibleClinicians}
                      ineligibleClinicians={ineligibleClinicians}
                    />
                  </>
                )}

                <Separator />

                {/* Improvement Opportunity */}
                {hasEligibilityData && config && (
                  <ImprovementOpportunity
                    currentScore={score}
                    currentAmount={dollarAmount}
                    medicareRevenue={medicareRevenue}
                    lowScenarioInput={lowScenarioInput}
                    scalingFactor={scalingFactor}
                  />
                )}

                {/* Disclaimer */}
                <div className="rounded-lg bg-muted/50 p-4 text-xs text-muted-foreground">
                  <p className="font-medium mb-2">Important:</p>
                  <p>
                    These projections are estimates based on current MIPS scores and Medicare Part B allowed charges. 
                    Actual payment adjustments are applied to claims during the payment year and depend on CMS final 
                    calculations including budget neutrality scaling. Three scenarios (low/expected/high) reflect 
                    uncertainty in these factors. Non-MIPS-eligible clinicians do not receive individual adjustments 
                    but contribute to group performance when reporting as a TIN.
                  </p>
                </div>
              </>
            )}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}

